import { Grid, GridSize, makeStyles, TableContainer, Paper } from "@material-ui/core";
import { Condominio } from "./ModalReplicarAcesso";
import InputSearch from "shared/components/input/InputSearch";
import { Tabela } from "shared/components/table/Tabela";
import { Col } from "react-bootstrap";
import { TableColumn } from "types/table/TableColumn";
import { LinhaTabela } from "shared/components/table/LinhaTabela";
import { RegistroTabela } from "shared/components/table/RegistroTabela";
import checkIcon from "static/cloud21/icones/check-icone.svg";
import { useState, useEffect, useMemo } from "react";
import { CheckBox } from "shared/components/checkbox/CheckBox";
import { OpcoesSelecionaveisCheckBox } from "types/OpcoesSelecionaveisCheckBoxType";
import { useGetGrupoCondominios } from "hooks/condominio/useGetGrupoCondominios";
import { GrupoCondominio } from "hooks/condominio/useGetGrupoCondominios";
import { api } from "constants/RotasAPI";
import SelectAsync from "shared/components/select/SelectAsync";
import CustomSwitch from "shared/components/Switch/CustomSwitch";
import { formatCNPJ } from "shared/util/formatters/cnpj";
import { limparCpfCnpj } from "shared/util/functions/formatacoesDeValores/FormatarValores";

const useStyles = makeStyles(() => ({
  linkAtivo: {
    fontFamily: "Open Sans, sans-serif",
    fontWeight: 700,
    fontSize: "0.8rem",
    color: "#49B6E7",
    cursor: "pointer",
  },
  linkInativo: {
    fontFamily: "Open Sans, sans-serif",
    fontWeight: 700,
    fontSize: "0.8rem",
    color: "#A8A8A8",
    cursor: "default",
  },
  tableContainer: {
    maxHeight: 500,
    "&::-webkit-scrollbar": {
      width: "8px",
    },
    "&::-webkit-scrollbar-track": {
      background: "#f1f1f1",
    },
    "&::-webkit-scrollbar-thumb": {
      backgroundColor: "#c1c1c1",
      borderRadius: "4px",
    },
    "&::-webkit-scrollbar-thumb:hover": {
      backgroundColor: "#a8a8a8",
    },
    "&::-webkit-scrollbar-button": {
      display: "none",
    },
  },
}));

type Props = {
  condominios: Condominio[];
  searchCondominio: string;
  setSearchCondominio: (value: string) => void;
  openModal?: (condominio: Condominio) => void;
  sizeInputSearch?: "small" | "medium" | "large" | GridSize;
  renderCheckbox?: boolean;
  onSelecionarCondominio?: (condominio: Condominio, selected: boolean) => void;
  idsSelecionados?: number[];
  handleSelecionarTodos?: (opcao: OpcoesSelecionaveisCheckBox, condominios?: Condominio[]) => void;
  isChecked?: boolean;
  centerTable?: boolean;
  tableSize?: GridSize;
  renderGrupoCondominios?: boolean;
  renderUsarGroupPay?: boolean;
  prefixId?: string;
  onChangeUsarGroupPay?: (isGroupPayAtivo: boolean, condominio: Condominio) => void;
  setSortString?: (value: string) => void;
  isWizard?: boolean;
  onCondominiosFiltradosChange?: (condominiosFiltrados: Condominio[]) => void;
};

const resolverGridSize = (value?: "small" | "medium" | "large" | GridSize): GridSize => {
  switch (value) {
    case "small":
      return 4;
    case "medium":
      return 6;
    case "large":
      return 12;
    case undefined:
      return 6;
    default:
      return value as GridSize;
  }
};

const TabelaConfigurarCondominios = ({
  condominios,
  setSearchCondominio,
  searchCondominio,
  openModal,
  sizeInputSearch,
  renderCheckbox,
  onSelecionarCondominio,
  idsSelecionados,
  handleSelecionarTodos,
  centerTable = false,
  tableSize = 8,
  renderGrupoCondominios = false,
  renderUsarGroupPay,
  onChangeUsarGroupPay,
  setSortString,
  prefixId = "",
  isWizard = false,
  onCondominiosFiltradosChange,
}: Props) => {
  const [ordenarPorColuna, setOrdenarPorColuna] = useState<string>("condominio");
  const [ordenacaoAscOuDesc, setOrdenacaoAscOuDesc] = useState<"ASC" | "DESC">("ASC");
  const [paginaAtual, setPaginaAtual] = useState(1);
  const { data: gruposData } = useGetGrupoCondominios();
  const [grupoCondominioSelecionado, setGrupoCondominioSelecionado] = useState<string | null>(null);

  const condominiosFiltrados = useMemo(() => {
    let listaBase: Condominio[] = [];

    if (grupoCondominioSelecionado && gruposData) {
      const grupoSelecionado = gruposData.find(
        (grupo: GrupoCondominio) => String(grupo.id) === grupoCondominioSelecionado
      );
      const condominiosDoGrupo = (grupoSelecionado?.condominios || []).map(
        (condominioDoGrupo: { id: any; pessoaJuridica: { nomeFantasia: any; cnpj: any } }) => ({
          ...condominioDoGrupo,
          condominioId: Number(condominioDoGrupo.id),
          nomeFantasia: condominioDoGrupo.pessoaJuridica?.nomeFantasia,
          cnpj: condominioDoGrupo.pessoaJuridica?.cnpj,
        })
      );
      const idsCondominiosProp = new Set(condominios.map((c) => c.condominioId));
      listaBase = condominiosDoGrupo.filter((cond: Condominio) => idsCondominiosProp.has(cond.condominioId));
    } else {
      listaBase = condominios;
    }

    if (searchCondominio) {
      const termoBuscaNome = searchCondominio.toLowerCase();
      const termoBuscaDocumento = limparCpfCnpj(searchCondominio);

      return listaBase.filter((condominio: Condominio) => {
        const matchNome = condominio.nomeFantasia?.toLowerCase().includes(termoBuscaNome);

        if (termoBuscaDocumento?.length > 0) {
          const cnpjNormalizadoDoCondominio =
            limparCpfCnpj(condominio.cnpj ?? "") || "";
          const matchDocumento = cnpjNormalizadoDoCondominio.includes(termoBuscaDocumento);

          return matchNome || matchDocumento;
        }

        return matchNome;
      });
    }

    return listaBase;
  }, [condominios, grupoCondominioSelecionado, searchCondominio, gruposData]);

  useEffect(() => {
    if (onCondominiosFiltradosChange) {
      onCondominiosFiltradosChange(condominiosFiltrados);
    }
  }, [condominiosFiltrados, onCondominiosFiltradosChange]);

  const todoscondominiosSelecionados = useMemo(() => {
    if (!condominiosFiltrados?.length || !idsSelecionados?.length) return false;
    return condominiosFiltrados.every((condominio) => idsSelecionados.includes(condominio.condominioId));
  }, [condominiosFiltrados, idsSelecionados]);

  const handleSelecionarTodosInterno = (opcao: OpcoesSelecionaveisCheckBox) => {
    if (handleSelecionarTodos) {
      handleSelecionarTodos(opcao, condominiosFiltrados);
    }
  };

  const classes = useStyles();

  const createColumns = (): TableColumn[] => {
    if (renderUsarGroupPay) {
      return [
        { id: "pessoaJuridica.nomeFantasia", label: "Condomínio", isOrdenavel: true, width: "35%" },
        { id: "pessoaJuridica.cnpj", label: "CNPJ", isOrdenavel: true, width: "30%" },
        { id: "statusGroupPay", label: "Usar Group Pay", isOrdenavel: false, width: "20%" },
        { id: "configurar", label: "", isOrdenavel: false, width: "27%" },
      ];
    }
    if (renderCheckbox) {
      return [
        { id: "pessoaJuridica.nomeFantasia", label: "Condomínio", isOrdenavel: true, width: "50%" },
        { id: "pessoaJuridica.cnpj", label: "CNPJ", isOrdenavel: true, width: "50%" },
      ];
    } else {
      return [
        { id: "check", label: "", isOrdenavel: false, width: "8%" },
        { id: "nomeFantasia", label: "Condomínio", isOrdenavel: true, width: "35%" },
        { id: "cnpj", label: "CNPJ", isOrdenavel: true, width: "30%" },
        { id: "configurar", label: "", isOrdenavel: false, width: "27%" },
      ];
    }
  };

  const ordenarRegistros = (idColuna: string) => {
    const novaOrdenacao =
      !ordenacaoAscOuDesc || ordenarPorColuna !== idColuna || ordenacaoAscOuDesc === "DESC" ? "ASC" : "DESC";
    setOrdenacaoAscOuDesc(novaOrdenacao);
    setOrdenarPorColuna(idColuna);
    setSortString?.(`${idColuna},${novaOrdenacao}`);
  };

  const RenderRegistros = ({ condominios }: { condominios: Condominio[] }) => {
    return (
      <>
        {condominios?.map((condominio: Condominio) => {
          const id = condominio.condominioId;
          const nomeParaExibir = condominio.nomeFantasia;
          const cnpjParaExibir = condominio.cnpj ? formatCNPJ(condominio.cnpj) : condominio.cnpj;

          return (
            <LinhaTabela
              id={`${prefixId}id-condominio-group-pay-${id}`}
              key={id}
              isSelecionada={idsSelecionados?.includes(id)}
            >
              {renderCheckbox ? (
                <RegistroTabela isCheckBox id={`check-box-condominio-${id}`} key={`check-box-condominio-${id}`}>
                  <CheckBox
                    id={`check-box-${id}`}
                    usarValidacaoParaFormulario={false}
                    key={`check-box-${id}`}
                    checked={idsSelecionados?.includes(id)}
                    onChange={(checked: boolean) => {
                      onSelecionarCondominio?.(condominio, checked);
                    }}
                  />
                </RegistroTabela>
              ) : (
                !renderUsarGroupPay && (
                  <RegistroTabela
                    id={`configurar-condominio-group-pay-${id}`}
                    key={`key-configurar-condominio-group-pay-${id}`}
                  >
                    {isWizard && condominio.possuiGroupPayConfigurado && <img src={checkIcon} alt="icone-check" />}
                  </RegistroTabela>
                )
              )}
              <RegistroTabela
                id={`configurar-condominio-group-pay-${id}`}
                key={`key-configurar-condominio-group-pay-${id}`}
              >
                {nomeParaExibir}
              </RegistroTabela>
              <RegistroTabela
                id={`configurar-condominio-group-pay-cnpj-${id}`}
                key={`key-configurar-condominio-group-pay-cnpj-${id}`}
              >
                {cnpjParaExibir}
              </RegistroTabela>
              {renderUsarGroupPay && (
                <RegistroTabela
                  id={`configurar-condominio-group-pay-status-${id}`}
                  key={`key-configurar-condominio-group-pay-status-${id}`}
                >
                  <CustomSwitch
                    labelDireito={condominio.possuiGroupPayAtivo ? "Ativo" : "Desativado"}
                    checked={condominio.possuiGroupPayAtivo}
                    onChange={() => onChangeUsarGroupPay?.(condominio.possuiGroupPayAtivo, condominio)}
                    usarValidacaoParaFormulario={false}
                  />
                </RegistroTabela>
              )}
              {!renderCheckbox && (
                <RegistroTabela
                  id={`configurar-condominio-group-pay-configurar-${id}`}
                  key={`key-configurar-condominio-group-pay-configurar-${id}`}
                >
                  <span
                    onClick={() => {
                      if (condominio.possuiGroupPayAtivo && openModal) openModal(condominio);
                    }}
                    className={condominio.possuiGroupPayAtivo ? classes.linkAtivo : classes.linkInativo}
                  >
                    Configurar consulta {">"}
                  </span>
                </RegistroTabela>
              )}
            </LinhaTabela>
          );
        })}
      </>
    );
  };

  return (
    <Grid container xs={12}>
      <Grid
        item
        xs={resolverGridSize(sizeInputSearch)}
        style={{
          marginTop: "1rem",
          marginLeft: centerTable ? "5rem" : "0",
        }}
      >
        <Grid container spacing={2} alignItems="flex-end">
          <Grid item xs={renderGrupoCondominios ? 8 : 12}>
            <InputSearch
              id="buscar-condominios-integrados-group-pay"
              label="Buscar por condomínio ou CNPJ"
              placeholder="Informe aqui a sua busca"
              onSearchChange={setSearchCondominio}
              value={searchCondominio}
              autoComplete="off"
            />
          </Grid>
          {renderGrupoCondominios && gruposData?.length > 0 && (
            <Grid item xs={4}>
              <SelectAsync<GrupoCondominio>
                id="select-grupo-condominios"
                nomeAtributoValue="id"
                nomeAtributoLabel="nome"
                endpoint={api.condominio.getGrupoCondominios}
                label="Filtrar por grupo de condomínios"
                placeholder="Selecione"
                isClearable={true}
                onChange={(selectedOption) => {
                  const selectedValue = selectedOption?.value ?? null;
                  setGrupoCondominioSelecionado(selectedValue);
                }}
                defaultValue={
                  gruposData?.find((grupo: GrupoCondominio) => String(grupo.id) === grupoCondominioSelecionado) || null
                }
                atributosRefresh={[gruposData]}
                usarValidacaoParaFormulario={false}
              />
            </Grid>
          )}
        </Grid>
      </Grid>
      {centerTable ? (
        <Grid item xs={12} style={{ marginTop: "1rem", display: "flex", justifyContent: "center" }}>
          <Grid item xs={tableSize}>
            <Col xs={12}>
              <Paper>
                <TableContainer className={classes.tableContainer}>
                  <Tabela
                    id="tabela-configuracao-condominios-integrados-group-pay"
                    colunas={createColumns()}
                    tipoCheckBox={"SELECT_ALL"}
                    renderCheckBox={renderCheckbox || false}
                    ordernarPorColuna={ordenarPorColuna}
                    ordenacaoAscOuDesc={ordenacaoAscOuDesc}
                    ordenarRegistrosFunction={(idColuna) => ordenarRegistros(idColuna)}
                    onChangePageFunction={(paginaAtual: number) => setPaginaAtual(paginaAtual)}
                    paginaAtual={paginaAtual}
                    quantidadeDePaginas={condominiosFiltrados?.length ?? 0}
                    isLoading={false}
                    quantidadeDeRegistros={condominiosFiltrados?.length ?? 0}
                    renderPagination={false}
                    onChangeCheckBox={handleSelecionarTodosInterno}
                    checkedCheckBox={todoscondominiosSelecionados}
                  >
                    <RenderRegistros condominios={condominiosFiltrados} />
                  </Tabela>
                </TableContainer>
              </Paper>
            </Col>
          </Grid>
        </Grid>
      ) : (
        <Grid item xs={12} style={{ marginTop: "1rem" }}>
          <Col xs={12}>
            <Paper>
              <TableContainer className={classes.tableContainer}>
                <Tabela
                  id="tabela-configuracao-condominios-integrados-group-pay"
                  colunas={createColumns()}
                  tipoCheckBox={"SELECT_ALL"}
                  renderCheckBox={renderCheckbox || false}
                  ordernarPorColuna={ordenarPorColuna}
                  ordenacaoAscOuDesc={ordenacaoAscOuDesc}
                  ordenarRegistrosFunction={(idColuna) => ordenarRegistros(idColuna)}
                  onChangePageFunction={(paginaAtual: number) => setPaginaAtual(paginaAtual)}
                  paginaAtual={paginaAtual}
                  quantidadeDePaginas={condominiosFiltrados?.length ?? 0}
                  isLoading={false}
                  quantidadeDeRegistros={condominiosFiltrados?.length ?? 0}
                  renderPagination={false}
                  onChangeCheckBox={handleSelecionarTodosInterno}
                  checkedCheckBox={todoscondominiosSelecionados}
                >
                  <RenderRegistros condominios={condominiosFiltrados} />
                </Tabela>
              </TableContainer>
            </Paper>
          </Col>
        </Grid>
      )}
    </Grid>
  );
};

export default TabelaConfigurarCondominios;
